/**
 * useFinanceiroAssinaturasPaginated - Hook de paginação server-side para Assinaturas (Recorrência)
 *
 * Espelho de useFinanceiroCobrancasPaginated, consumindo a RPC
 * get_assinaturas_paginated que devolve linhas + KPIs (COUNT(*) OVER()) via JOIN
 * com customers / meio de pagamento / conta corrente.
 *
 * Zero acesso direto a tabelas no frontend — toda leitura é por RPC.
 *
 * @version 1.0.0
 * RELEASE: 2026-06-09 v1.0.0 - Criação inicial (Fase 4 do módulo Recorrência)
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('FinanceiroAssinaturasPaginated');

/** Linha de assinatura retornada pela RPC get_assinaturas_paginated */
export interface PaginatedAssinatura {
  id: string;
  tenant_id: string;
  customer_id: string;
  deal_id: string | null;
  status: string; // ativa | pausada | cancelada | finalizada
  frequencia: string;
  tipo_prazo: string; // determinado | indeterminado
  ciclos_total: number | null;
  ciclos_gerados: number;
  primeiro_vencimento: string;
  proximo_vencimento: string;
  dias_antes_emissao: number;
  valor_total: number;
  moeda: string;
  meio_pagamento_id: string;
  conta_corrente_id: string;
  descricao: string | null;
  provider_slug: string | null;
  created_at: string;
  updated_at: string;
  canceled_at: string | null;
  // joins
  customer_name: string | null;
  customer_document: string | null;
  tipo_pagamento: string | null;
  conta_corrente_nome: string | null;
  // agregações por linha
  ciclos_pendentes: number;
  ciclos_pagos: number;
  // KPIs globais (mesmos valores em todas as linhas)
  kpi_total: number;
  kpi_ativas: number;
  kpi_mrr: number;
  kpi_ciclos_pendentes: number;
  // paginação
  total_count: number;
}

/** Filtros aceitos pela RPC */
export interface AssinaturaPaginatedFilters {
  status?: string | null;
  frequencia?: string | null;
  meio_pagamento_id?: string | null;
  customer_id?: string | null;
  deal_id?: string | null;
  search?: string | null;
  proximo_venc_de?: string | null;
  proximo_venc_ate?: string | null;
  /** NOVO: filtra assinaturas vinculadas a um veículo */
  vehicle_id?: string | null;
  /** v1.1.0: recorte por data de criação (drill-down dos KPIs do dashboard) */
  criado_de?: string | null;
  criado_ate?: string | null;
  /** v1.1.0: recorte por data de cancelamento (drill-down de perdas) */
  cancelado_de?: string | null;
  cancelado_ate?: string | null;
}

interface PaginationParams {
  page: number;
  pageSize: number;
}

export interface AssinaturaKpis {
  total: number;
  ativas: number;
  mrr: number; // MRR estimado pela RPC (com fator de frequência)
  ciclosPendentes: number;
}

export interface PaginatedAssinaturasResult {
  assinaturas: PaginatedAssinatura[];
  totalCount: number;
  totalPages: number;
  kpis: AssinaturaKpis;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Busca assinaturas paginadas (server-side) com KPIs já agregados.
 */
export function useFinanceiroAssinaturasPaginated(
  filters: AssinaturaPaginatedFilters | undefined,
  pagination: PaginationParams,
): PaginatedAssinaturasResult {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['financeiro-assinaturas-paginated', currentTenant?.id, filters, pagination],
    queryFn: async () => {
      logger.debug('Buscando assinaturas paginadas', { filters, pagination });

      // Casting até regeneração dos types
      const { data, error } = await (supabase.rpc as any)('get_assinaturas_paginated', {
        p_page: pagination.page,
        p_page_size: pagination.pageSize,
        p_tenant_hint: currentTenant?.id || null,
        p_status: filters?.status || null,
        p_frequencia: filters?.frequencia || null,
        p_meio_pagamento_id: filters?.meio_pagamento_id || null,
        p_customer_id: filters?.customer_id || null,
        p_deal_id: filters?.deal_id || null,
        p_search: filters?.search || null,
        p_proximo_venc_de: filters?.proximo_venc_de || null,
        p_proximo_venc_ate: filters?.proximo_venc_ate || null,
        // NOVO: filtro por veículo (módulo Veículos)
        p_vehicle_id: filters?.vehicle_id || null,
        // v1.1.0: recortes por criação/cancelamento usados pelo dashboard
        p_criado_de: filters?.criado_de || null,
        p_criado_ate: filters?.criado_ate || null,
        p_cancelado_de: filters?.cancelado_de || null,
        p_cancelado_ate: filters?.cancelado_ate || null,
      });

      if (error) {
        logger.error('Erro ao buscar assinaturas paginadas', error);
        throw error;
      }

      const rows = (data || []) as PaginatedAssinatura[];
      const totalCount = Number(rows[0]?.total_count ?? 0);
      const kpis: AssinaturaKpis = {
        total: Number(rows[0]?.kpi_total ?? 0),
        ativas: Number(rows[0]?.kpi_ativas ?? 0),
        mrr: Number(rows[0]?.kpi_mrr ?? 0),
        ciclosPendentes: Number(rows[0]?.kpi_ciclos_pendentes ?? 0),
      };

      logger.debug(`Recebidas ${rows.length} assinaturas (total ${totalCount})`);

      return {
        assinaturas: rows,
        totalCount,
        totalPages: Math.ceil(totalCount / pagination.pageSize) || 1,
        kpis,
      };
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    assinaturas: query.data?.assinaturas || [],
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 1,
    kpis: query.data?.kpis || { total: 0, ativas: 0, mrr: 0, ciclosPendentes: 0 },
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: () => {
      queryClient.invalidateQueries({
        queryKey: ['financeiro-assinaturas-paginated', currentTenant?.id],
      });
    },
  };
}
